// src/AddInventoryItemView.js
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useSettings } from './contexts/SettingsContext';

const AddInventoryItemView = ({ setCurrentView, addInventoryItem }) => {
    const { showAlert } = useAlert();
    const { appSettings } = useSettings();

    const [newInventoryItem, setNewInventoryItem] = useState({
        name: '', itemType: '', quantity: '', unit: '', expiryDate: '', location: '',
        supplier: '', reorderThreshold: '', costPrice: '', sellingPrice: '',
    });

    const handleAddInventoryItem = async () => {
        if (!newInventoryItem.name || !newInventoryItem.itemType || !newInventoryItem.quantity || !newInventoryItem.unit || !newInventoryItem.expiryDate || !newInventoryItem.location) {
            showAlert('Please fill in all required inventory fields.', 'error');
            return;
        }

        try {
            const inventoryOrgId = appSettings.id;
            const expiryDateISO = `${newInventoryItem.expiryDate}T00:00:00`;
            await addInventoryItem({
                name: newInventoryItem.name,
                itemType: newInventoryItem.itemType,
                quantity: parseInt(newInventoryItem.quantity),
                unit: newInventoryItem.unit,
                expiryDate: expiryDateISO,
                location: newInventoryItem.location,
                supplier: newInventoryItem.supplier || null,
                reorderThreshold: newInventoryItem.reorderThreshold ? parseInt(newInventoryItem.reorderThreshold) : null,
                costPrice: newInventoryItem.costPrice ? parseFloat(newInventoryItem.costPrice) : null,
                sellingPrice: newInventoryItem.sellingPrice ? parseFloat(newInventoryItem.sellingPrice) : null,
                organizationId: inventoryOrgId,
            });
            setNewInventoryItem({
                name: '', itemType: '', quantity: '', unit: '', expiryDate: '', location: '',
                supplier: '', reorderThreshold: '', costPrice: '', sellingPrice: '',
            });
            showAlert('Inventory item added successfully!', 'success');
            setCurrentView('inventory');
        } catch (error) {
            console.error('Failed to add inventory item:', error);
            showAlert('Failed to add item. Please try again.', 'error');
        }
    };

    // Format price inputs to add .00 only if no decimal is provided
    const formatPriceInput = (value) => {
        if (value === '' || isNaN(parseFloat(value))) return '';
        
        // If the value already contains a decimal point, return as is
        if (value.includes('.')) {
            return value;
        }
        
        // If it's a whole number, add .00
        return `${value}.00`;
    };

    const handlePriceChange = (field, value) => {
        // Allow typing without immediate formatting
        setNewInventoryItem({ ...newInventoryItem, [field]: value });
    };

    const handlePriceBlur = (field, value) => {
        // Format only when user leaves the field (onBlur)
        const formattedValue = formatPriceInput(value);
        setNewInventoryItem({ ...newInventoryItem, [field]: formattedValue });
    };

    return (
        <div className="space-y-6 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-gray-800">Add New Inventory Item</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleAddInventoryItem(); }} className="space-y-4">
                <div>
                    <label htmlFor="itemName" className="block text-sm font-medium text-gray-700">Item Name</label>
                    <input
                        type="text"
                        id="itemName"
                        value={newInventoryItem.name}
                        onChange={(e) => setNewInventoryItem({ ...newInventoryItem, name: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="itemType" className="block text-sm font-medium text-gray-700">Item Type</label>
                    <select
                        id="itemType"
                        value={newInventoryItem.itemType}
                        onChange={(e) => setNewInventoryItem({ ...newInventoryItem, itemType: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                    >
                        <option value="">Select Type</option>
                        <option value="Medication">Medication</option>
                        <option value="Supply">Supply</option>
                        <option value="Equipment">Equipment</option>
                    </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantity</label>
                        <input
                            type="number"
                            id="quantity"
                            value={newInventoryItem.quantity}
                            onChange={(e) => setNewInventoryItem({ ...newInventoryItem, quantity: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="unit" className="block text-sm font-medium text-gray-700">Unit</label>
                        <input
                            type="text"
                            id="unit"
                            value={newInventoryItem.unit}
                            onChange={(e) => setNewInventoryItem({ ...newInventoryItem, unit: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., pills, ml, units"
                            required
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">Expiry Date</label>
                    <input
                        type="date"
                        id="expiryDate"
                        value={newInventoryItem.expiryDate}
                        onChange={(e) => setNewInventoryItem({ ...newInventoryItem, expiryDate: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                        type="text"
                        id="location"
                        value={newInventoryItem.location}
                        onChange={(e) => setNewInventoryItem({ ...newInventoryItem, location: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Pharmacy, Storage Room A, Ward 3"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">Supplier (Optional)</label>
                    <input
                        type="text"
                        id="supplier"
                        value={newInventoryItem.supplier}
                        onChange={(e) => setNewInventoryItem({ ...newInventoryItem, supplier: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., MedSupply Inc."
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="reorderThreshold" className="block text-sm font-medium text-gray-700">Reorder Threshold (Optional)</label>
                        <input
                            type="number"
                            id="reorderThreshold"
                            value={newInventoryItem.reorderThreshold}
                            onChange={(e) => setNewInventoryItem({ ...newInventoryItem, reorderThreshold: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="costPrice" className="block text-sm font-medium text-gray-700">Cost Price (Optional)</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{appSettings.currency?.symbol || 'Ksh.'}</span>
                            <input
                                type="number"
                                id="costPrice"
                                value={newInventoryItem.costPrice}
                                onChange={(e) => handlePriceChange('costPrice', e.target.value)}
                                onBlur={(e) => handlePriceBlur('costPrice', e.target.value)}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 pl-10 focus:ring-blue-500 focus:border-blue-500"
                                step="0.01"
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <label htmlFor="sellingPrice" className="block text-sm font-medium text-gray-700">Selling Price (Optional)</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{appSettings.currency?.symbol || 'Ksh.'}</span>
                        <input
                            type="number"
                            id="sellingPrice"
                            value={newInventoryItem.sellingPrice}
                            onChange={(e) => handlePriceChange('sellingPrice', e.target.value)}
                            onBlur={(e) => handlePriceBlur('sellingPrice', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 pl-10 focus:ring-blue-500 focus:border-blue-500"
                            step="0.01"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setNewInventoryItem({
                                name: '', itemType: '', quantity: '', unit: '', expiryDate: '', location: '',
                                supplier: '', reorderThreshold: '', costPrice: '', sellingPrice: '',
                            });
                            setCurrentView('inventory');
                        }}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Plus className="w-4 h-4" />
                        Add Item
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddInventoryItemView;